import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { nomeDeExibicao } from '@/lib/usuario';
import { useConvites } from '@/pages/convites/context';
import { TIPOS_EVENTO } from '@/pages/home/utils';
import Logo from '@/components/ui/Logo';
import Modal from '@/components/ui/Modal';
import EntrarNaFuncao from '@/components/feature/EntrarNaFuncao';

interface PortariaEvento {
  evento_id: number;
  evento: string;
  inicio_em: string | null;
}

// "dd/mm" no horário de Brasília.
function dataCurta(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
  }).format(d);
}

interface ListaMenuProps {
  organiza: boolean;
  alto: boolean;
  totalConvites: number;
  temRifas: boolean;
  temBarraca: boolean;
  temCaixa: boolean;
  temFichas: boolean;
  portariaEventos: PortariaEvento[];
  adminPlataforma: boolean;
  aoAbrirPortaria: () => void;
  aoNavegar: (para: string) => void;
  aoEntrarFuncao: () => void;
  aoSair: () => void;
}

function ListaMenu({ organiza, alto, totalConvites, temRifas, temBarraca, temCaixa, temFichas, portariaEventos, adminPlataforma, aoAbrirPortaria, aoNavegar, aoEntrarFuncao, aoSair }: ListaMenuProps) {
  const iconeCls = alto ? 'h-5 w-5' : 'h-4 w-4';
  const itemCls = alto
    ? 'flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 min-h-12 text-left text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100'
    : 'flex w-full cursor-pointer items-center gap-2.5 px-4 py-2.5 text-left text-sm text-foreground-800 transition-colors hover:bg-background-100';

  const renderItem = (para: string, icone: string, rotulo: string) => (
    <button key={para} type="button" onClick={() => aoNavegar(para)} className={itemCls}>
      <span className={`flex items-center justify-center text-primary-600 ${iconeCls}`}>
        <i className={icone} />
      </span>
      {rotulo}
    </button>
  );

  const abrirPortaria = () => {
    if (portariaEventos.length === 1) {
      aoNavegar(`/portaria/${portariaEventos[0].evento_id}`);
    } else {
      aoAbrirPortaria();
    }
  };

  return (
    <>
      {totalConvites > 0 && (
        <button type="button" onClick={() => aoNavegar('/convites')} className={itemCls}>
          <span className={`flex items-center justify-center text-primary-600 ${iconeCls}`}>
            <i className="ri-mail-line" />
          </span>
          Convites ({totalConvites})
        </button>
      )}

      {renderItem('/minhas-inscricoes', 'ri-file-list-3-line', 'Minhas inscrições')}
      <button type="button" onClick={aoEntrarFuncao} className={itemCls}>
        <span className={`flex items-center justify-center text-primary-600 ${iconeCls}`}>
          <i className="ri-qr-scan-2-line" />
        </span>
        Entrar numa função
      </button>
      {renderItem('/meus-ingressos', 'ri-ticket-2-line', 'Meus ingressos e fichas')}
      {temRifas && renderItem('/minhas-rifas', 'ri-coupon-3-line', 'Ação entre amigos')}
      {temBarraca && renderItem('/loja/barraca', 'ri-store-2-line', 'Minha barraca')}
      {temCaixa && renderItem('/caixa', 'ri-money-dollar-circle-line', 'Caixa')}
      {portariaEventos.length > 0 && (
        <button type="button" onClick={abrirPortaria} className={itemCls}>
          <span className={`flex items-center justify-center text-primary-600 ${iconeCls}`}>
            <i className="ri-qr-scan-2-line" />
          </span>
          Portaria
        </button>
      )}
      {renderItem('/meu-perfil', 'ri-user-line', 'Meu perfil')}

      <div className="my-1 h-px bg-secondary-200" />

      {organiza && (
        <button type="button" onClick={() => aoNavegar('/meus-eventos')} className={itemCls}>
          <span className={`flex items-center justify-center text-primary-600 ${iconeCls}`}>
            <i className="ri-calendar-check-line" />
          </span>
          Meus eventos
        </button>
      )}
      <button type="button" onClick={() => aoNavegar('/criar')} className={itemCls}>
        <span className={`flex items-center justify-center text-primary-600 ${iconeCls}`}>
          <i className="ri-add-line" />
        </span>
        Novo evento
      </button>
      {renderItem('/conheca', 'ri-question-answer-line', 'Como funciona')}
      {adminPlataforma && renderItem('/plataforma', 'ri-shield-star-line', 'Painel da plataforma')}

      <div className="my-1 h-px bg-secondary-200" />

      <button type="button" onClick={aoSair} className={itemCls}>
        <span className={`flex items-center justify-center text-primary-600 ${iconeCls}`}>
          <i className="ri-logout-box-r-line" />
        </span>
        Sair
      </button>
    </>
  );
}

function BarraExplorar() {
  const [aberto, setAberto] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fechar = (ev: MouseEvent) => {
      if (ref.current && !ref.current.contains(ev.target as Node)) setAberto(false);
    };
    document.addEventListener('mousedown', fechar);
    return () => document.removeEventListener('mousedown', fechar);
  }, []);

  return (
    <div ref={ref} className="relative hidden lg:block">
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100 hover:text-primary-700"
      >
        Explorar
        <span
          className={`flex h-4 w-4 items-center justify-center transition-transform ${aberto ? 'rotate-180' : ''}`}
        >
          <i className="ri-arrow-down-s-line" />
        </span>
      </button>

      {aberto && (
        <div className="absolute left-0 top-full z-50 mt-2 w-60 overflow-hidden rounded-xl border border-secondary-200 bg-white py-1 shadow-lg">
          {TIPOS_EVENTO.map((t) => (
            <Link
              key={t.valor}
              to={`/?tipo=${t.valor}`}
              onClick={() => setAberto(false)}
              className="flex w-full cursor-pointer items-center gap-2.5 px-4 py-2.5 text-left text-sm text-foreground-800 transition-colors hover:bg-background-100"
            >
              <span className="text-foreground-400">{t.rotulo}</span>
            </Link>
          ))}
          <div className="my-1 h-px bg-secondary-200" />
          <Link
            to="/agenda"
            onClick={() => setAberto(false)}
            className="flex w-full cursor-pointer items-center gap-2.5 px-4 py-2.5 text-left text-sm font-medium text-primary-700 transition-colors hover:bg-background-100"
          >
            Ver a agenda completa
          </Link>
        </div>
      )}
    </div>
  );
}

export default function Topo() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const ehHome = location.pathname === '/';
  // No celular a barra do site some na Início (logado) e na página do evento
  // (lá ficam só os botões redondos sobre a capa). No computador continua.
  const [logado, setLogado] = useState(false);
  const [primeiroNome, setPrimeiroNome] = useState('');
  const [organiza, setOrganiza] = useState(false);
  const [temRifas, setTemRifas] = useState(false);
  const [temBarraca, setTemBarraca] = useState(false);
  const [temCaixa, setTemCaixa] = useState(false);
  const [portariaEventos, setPortariaEventos] = useState<PortariaEvento[]>([]);
  const [adminPlataforma, setAdminPlataforma] = useState(false);
  const [temFichas, setTemFichas] = useState(false);
  const [portariaAberto, setPortariaAberto] = useState(false);
  const [sairConfirmar, setSairConfirmar] = useState(false);
  const [funcaoAberto, setFuncaoAberto] = useState(false);
  const [menuAberto, setMenuAberto] = useState(false);
  const [mobileMenuAberto, setMobileMenuAberto] = useState(false);
  const [busca, setBusca] = useState(searchParams.get('q') ?? '');
  const [cidade, setCidade] = useState(searchParams.get('cidade') ?? '');
  const [cidades, setCidades] = useState<string[]>([]);
  const menuRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);

  const { convites } = useConvites();
  const totalConvites = convites.length;

  useEffect(() => {
    setBusca(searchParams.get('q') ?? '');
    setCidade(searchParams.get('cidade') ?? '');
  }, [searchParams]);

  useEffect(() => {
    let ativo = true;
    supabase
      .from('eventos')
      .select('cidade')
      .eq('status', 'publicado')
      .then(({ data }) => {
        if (!ativo) return;
        setCidades(
          [...new Set(((data as { cidade: string | null }[]) || []).map((c) => c.cidade).filter((c): c is string => !!c))].sort(),
        );
      });
    return () => {
      ativo = false;
    };
  }, []);

  // "Minhas fichas" aparece quando este celular guarda alguma carteira (logado ou não).
  useEffect(() => {
    try {
      let achou = false;
      for (let i = 0; i < localStorage.length; i += 1) {
        const k = localStorage.key(i);
        if (k && k.startsWith('carteira:')) {
          achou = true;
          break;
        }
      }
      setTemFichas(achou);
    } catch {
      setTemFichas(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    let ativo = true;

    supabase.auth.getSession().then(async ({ data }) => {
      if (!ativo) return;
      const user = data.session?.user;
      if (user) {
        setLogado(true);
        nomeDeExibicao(user).then((n) => {
          if (ativo) setPrimeiroNome(n);
        });
        const { data: evs } = await supabase.rpc('meus_eventos');
        if (ativo) setOrganiza(((evs as unknown[]) || []).length > 0);
        const { data: rf } = await supabase.rpc('minhas_rifas');
        if (ativo) setTemRifas(((rf as unknown[]) || []).length > 0);
        const { data: pts } = await supabase.rpc('loja_meus_pontos');
        if (ativo) setTemBarraca(((pts as unknown[]) || []).length > 0);
        const { data: cx } = await supabase.rpc('caixa_meus_eventos');
        if (ativo) setTemCaixa(((cx as unknown[]) || []).length > 0);
        const { data: pt } = await supabase.rpc('portaria_meus_eventos');
        if (ativo) setPortariaEventos(((pt as PortariaEvento[]) || []));
        const { data: adm } = await supabase.rpc('sou_admin_plataforma');
        if (ativo) setAdminPlataforma(adm === true);
      }
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!ativo) return;
      const user = session?.user;
      setLogado(!!user);
      if (user) {
        nomeDeExibicao(user).then((n) => {
          if (ativo) setPrimeiroNome(n);
        });
      } else {
        setPrimeiroNome('');
      }
      if (user) {
        supabase.rpc('meus_eventos').then(({ data: evs }) => {
          if (ativo) setOrganiza(((evs as unknown[]) || []).length > 0);
        });
        supabase.rpc('minhas_rifas').then(({ data: rf }) => {
          if (ativo) setTemRifas(((rf as unknown[]) || []).length > 0);
        });
        supabase.rpc('loja_meus_pontos').then(({ data: pts }) => {
          if (ativo) setTemBarraca(((pts as unknown[]) || []).length > 0);
        });
        supabase.rpc('caixa_meus_eventos').then(({ data: cx }) => {
          if (ativo) setTemCaixa(((cx as unknown[]) || []).length > 0);
        });
        supabase.rpc('portaria_meus_eventos').then(({ data: pt }) => {
          if (ativo) setPortariaEventos(((pt as PortariaEvento[]) || []));
        });
        supabase.rpc('sou_admin_plataforma').then(({ data: adm }) => {
          if (ativo) setAdminPlataforma(adm === true);
        });
      } else {
        setOrganiza(false);
        setTemRifas(false);
        setTemBarraca(false);
        setTemCaixa(false);
        setPortariaEventos([]);
        setAdminPlataforma(false);
      }
    });

    return () => {
      ativo = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const fechar = (ev: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(ev.target as Node)) {
        setMenuAberto(false);
      }
      if (mobileRef.current && !mobileRef.current.contains(ev.target as Node)) {
        setMobileMenuAberto(false);
      }
    };
    document.addEventListener('mousedown', fechar);
    return () => document.removeEventListener('mousedown', fechar);
  }, []);

  // Ao aceitar um convite, "Meus eventos" pode acabar de surgir no menu.
  useEffect(() => {
    let ativo = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!ativo || !data.session?.user) return;
      supabase.rpc('meus_eventos').then(({ data: evs }) => {
        if (ativo) setOrganiza(((evs as unknown[]) || []).length > 0);
      });
      supabase.rpc('minhas_rifas').then(({ data: rf }) => {
        if (ativo) setTemRifas(((rf as unknown[]) || []).length > 0);
      });
      supabase.rpc('loja_meus_pontos').then(({ data: pts }) => {
        if (ativo) setTemBarraca(((pts as unknown[]) || []).length > 0);
      });
      supabase.rpc('caixa_meus_eventos').then(({ data: cx }) => {
        if (ativo) setTemCaixa(((cx as unknown[]) || []).length > 0);
      });
      supabase.rpc('portaria_meus_eventos').then(({ data: pt }) => {
        if (ativo) setPortariaEventos(((pt as PortariaEvento[]) || []));
      });
      supabase.rpc('sou_admin_plataforma').then(({ data: adm }) => {
        if (ativo) setAdminPlataforma(adm === true);
      });
    });
    return () => {
      ativo = false;
    };
  }, [totalConvites]);

  const sair = () => {
    setMenuAberto(false);
    setMobileMenuAberto(false);
    setSairConfirmar(true);
  };

  const sairDeVez = async () => {
    setSairConfirmar(false);
    // Só neste celular (scope local): não derruba a conta nos outros aparelhos.
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } finally {
      navigate('/');
    }
  };

  const abrirFuncao = () => {
    setMenuAberto(false);
    setMobileMenuAberto(false);
    setFuncaoAberto(true);
  };

  const ir = (para: string) => {
    setMenuAberto(false);
    setMobileMenuAberto(false);
    navigate(para);
  };

  const abrirPortaria = () => {
    setMenuAberto(false);
    setMobileMenuAberto(false);
    setPortariaAberto(true);
  };

  const buscar = () => {
    const params = new URLSearchParams();
    if (busca.trim()) params.set('q', busca.trim());
    if (cidade) params.set('cidade', cidade);
    const qs = params.toString();
    navigate(`/${qs ? `?${qs}` : ''}`);
  };

  return (
    <header className="sticky top-0 z-40 hidden w-full border-b border-secondary-200/60 bg-white md:block">
      <div className="mx-auto flex h-16 w-full max-w-[1400px] items-center justify-between gap-4 px-4 md:px-6">
        {/* Logo + navegação */}
        <div className="flex items-center gap-6">
          <Link to="/" className="flex cursor-pointer items-center gap-2">
            <Logo />
            {!ehHome && (
              <span className="hidden items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-2 text-sm font-medium text-foreground-600 transition-colors hover:bg-background-100 hover:text-foreground-900 sm:flex">
                <span className="flex h-5 w-5 items-center justify-center">
                  <i className="ri-arrow-left-line" />
                </span>
                <span>Voltar</span>
              </span>
            )}
          </Link>

          <nav className="hidden items-center gap-1 md:flex">
            <Link
              to="/"
              aria-current={ehHome ? 'page' : undefined}
              className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-2 text-sm font-medium transition-colors ${
                ehHome
                  ? 'text-primary-700'
                  : 'text-foreground-800 hover:bg-background-100 hover:text-primary-700'
              }`}
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-home-5-line" />
              </span>
              Início
            </Link>
            <Link
              to="/agenda"
              className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100 hover:text-primary-700"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-calendar-2-line" />
              </span>
              Agenda
            </Link>
            <BarraExplorar />
          </nav>
        </div>

        {/* Localização + busca + entrar */}
        <div className="flex items-center gap-3">
          {/* Localização */}
          <div className="relative hidden items-center gap-1.5 lg:flex">
            <span className="flex h-4 w-4 items-center justify-center text-primary-600">
              <i className="ri-map-pin-2-fill" />
            </span>
            <select
              value={cidade}
              onChange={(ev) => {
                setCidade(ev.target.value);
                const params = new URLSearchParams(searchParams);
                if (ev.target.value) params.set('cidade', ev.target.value);
                else params.delete('cidade');
                const qs = params.toString();
                navigate(`/${qs ? `?${qs}` : ''}`);
              }}
              className="cursor-pointer appearance-none rounded-md border border-secondary-200 bg-white py-1.5 pl-2 pr-7 text-sm font-medium text-foreground-800 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            >
              <option value="">Todas as cidades</option>
              {cidades.map((c) => (
                <option key={c} value={c}>
                  {c}, SP
                </option>
              ))}
            </select>
          </div>

          {/* Busca */}
          <div className="relative hidden w-full max-w-[300px] items-center lg:flex">
            <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-search-line" />
            </span>
            <input
              type="text"
              value={busca}
              onChange={(ev) => setBusca(ev.target.value)}
              onKeyDown={(ev) => {
                if (ev.key === 'Enter') buscar();
              }}
              placeholder="Buscar eventos, cidades..."
              className="w-full rounded-full border border-secondary-200 bg-background-50 py-2 pl-9 pr-4 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
            />
          </div>


          {logado ? (
            <div ref={menuRef} className="relative hidden md:block">
              <button
                type="button"
                onClick={() => setMenuAberto((v) => !v)}
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full px-3 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-500 text-sm font-semibold text-white">
                  {primeiroNome ? (
                    primeiroNome.charAt(0).toUpperCase()
                  ) : (
                    <i className="ri-user-3-line text-base" aria-hidden />
                  )}
                </span>
                <span className="hidden sm:inline">{primeiroNome}</span>
                {totalConvites > 0 && (
                  <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-danger-500 px-1.5 text-xs font-semibold text-white">
                    {totalConvites}
                  </span>
                )}
                <span className="flex h-4 w-4 items-center justify-center text-foreground-500">
                  <i className={`ri-arrow-down-s-line transition-transform ${menuAberto ? 'rotate-180' : ''}`} />
                </span>
              </button>

              {menuAberto && (
                <div className="absolute right-0 top-full z-50 mt-2 w-56 overflow-hidden rounded-xl border border-secondary-200 bg-white py-1 shadow-lg">
                  <ListaMenu organiza={organiza} alto={false} totalConvites={totalConvites} temRifas={temRifas} temBarraca={temBarraca} temCaixa={temCaixa} temFichas={temFichas} portariaEventos={portariaEventos} adminPlataforma={adminPlataforma} aoAbrirPortaria={abrirPortaria} aoNavegar={ir} aoEntrarFuncao={abrirFuncao} aoSair={sair} />
                </div>
              )}
            </div>
          ) : (
            <div className="hidden items-center gap-3 md:flex">
              <Link
                to="/conheca"
                className="cursor-pointer whitespace-nowrap text-sm font-medium text-foreground-700 transition-colors hover:text-primary-700"
              >
                Como funciona
              </Link>
              {temFichas && (
                <Link
                  to="/meus-ingressos"
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap text-sm font-medium text-foreground-700 transition-colors hover:text-primary-700"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-ticket-2-line" />
                  </span>
                  Meus ingressos e fichas
                </Link>
              )}
              <Link
                to="/entrar"
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-user-3-line" />
                </span>
                Entrar
              </Link>
            </div>
          )}

          <button
            type="button"
            onClick={() => setMobileMenuAberto((v) => !v)}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg text-foreground-700 transition-colors hover:bg-background-100 md:hidden"
            aria-label="Menu"
          >
            <i className="ri-menu-line text-xl" />
          </button>
        </div>
      </div>

      {mobileMenuAberto && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setMobileMenuAberto(false)} />
          <div ref={mobileRef} className="absolute right-0 top-0 h-full w-[280px] overflow-y-auto bg-white p-5 shadow-xl">
            <div className="flex items-center justify-between">
              <Logo />
              <button
                type="button"
                onClick={() => setMobileMenuAberto(false)}
                className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg text-foreground-500 transition-colors hover:bg-background-100"
                aria-label="Fechar menu"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>

            <div className="mt-4 flex items-center gap-2">
              <div className="relative flex-1 items-center">
                <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
                  <i className="ri-search-line" />
                </span>
                <input
                  type="text"
                  value={busca}
                  onChange={(ev) => setBusca(ev.target.value)}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter') {
                      setMobileMenuAberto(false);
                      buscar();
                    }
                  }}
                  placeholder="Buscar eventos..."
                  className="w-full rounded-full border border-secondary-200 bg-background-50 py-2 pl-9 pr-4 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                />
              </div>
            </div>

            <div className="mt-6 border-t border-secondary-200 pt-4">
              <Link
                to="/"
                onClick={() => setMobileMenuAberto(false)}
                className="flex min-h-12 cursor-pointer items-center gap-3 rounded-lg px-3 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
              >
                <span className="flex h-5 w-5 items-center justify-center text-primary-600">
                  <i className="ri-home-5-line" />
                </span>
                Início
              </Link>
              <Link
                to="/agenda"
                onClick={() => setMobileMenuAberto(false)}
                className="flex min-h-12 cursor-pointer items-center gap-3 rounded-lg px-3 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
              >
                <span className="flex h-5 w-5 items-center justify-center text-primary-600">
                  <i className="ri-calendar-2-line" />
                </span>
                Agenda
              </Link>

              {logado ? (
                <ListaMenu organiza={organiza} alto totalConvites={totalConvites} temRifas={temRifas} temBarraca={temBarraca} temCaixa={temCaixa} temFichas={temFichas} portariaEventos={portariaEventos} adminPlataforma={adminPlataforma} aoAbrirPortaria={abrirPortaria} aoNavegar={ir} aoEntrarFuncao={abrirFuncao} aoSair={sair} />
              ) : (
                <>
                  <Link
                    to="/conheca"
                    onClick={() => setMobileMenuAberto(false)}
                    className="flex min-h-12 cursor-pointer items-center gap-3 rounded-lg px-3 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
                  >
                    <span className="flex h-5 w-5 items-center justify-center text-primary-600">
                      <i className="ri-question-answer-line" />
                    </span>
                    Como funciona
                  </Link>
                  {temFichas && (
                    <Link
                      to="/meus-ingressos"
                      onClick={() => setMobileMenuAberto(false)}
                      className="flex min-h-12 cursor-pointer items-center gap-3 rounded-lg px-3 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
                    >
                      <span className="flex h-5 w-5 items-center justify-center text-primary-600">
                        <i className="ri-ticket-2-line" />
                      </span>
                      Meus ingressos e fichas
                    </Link>
                  )}
                  <Link
                    to="/entrar"
                    onClick={() => setMobileMenuAberto(false)}
                    className="flex min-h-12 cursor-pointer items-center gap-3 rounded-lg px-3 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
                  >
                    <span className="flex h-5 w-5 items-center justify-center text-primary-600">
                      <i className="ri-user-3-line" />
                    </span>
                    Entrar
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <Modal
        aberto={portariaAberto}
        titulo="Portaria"
        aoFechar={() => setPortariaAberto(false)}
      >
        <p className="text-sm text-foreground-500">Escolha o evento para abrir a portaria.</p>
        <div className="mt-3 space-y-2">
          {portariaEventos.map((ev) => (
            <button
              key={ev.evento_id}
              type="button"
              onClick={() => {
                setPortariaAberto(false);
                navigate(`/portaria/${ev.evento_id}`);
              }}
              className="flex w-full cursor-pointer items-center justify-between gap-3 rounded-2xl border border-secondary-200 bg-background-50 px-4 py-3 text-left transition-colors hover:bg-background-100"
            >
              <span className="flex flex-col">
                <span className="text-sm font-semibold text-foreground-950">{ev.evento}</span>
                {dataCurta(ev.inicio_em) && (
                  <span className="text-xs text-foreground-500">{dataCurta(ev.inicio_em)}</span>
                )}
              </span>
              <span className="flex h-5 w-5 shrink-0 items-center justify-center text-primary-600">
                <i className="ri-arrow-right-s-line" />
              </span>
            </button>
          ))}
        </div>
      </Modal>

      <Modal
        aberto={sairConfirmar}
        titulo="Sair deste celular?"
        aoFechar={() => setSairConfirmar(false)}
      >
        <p className="text-sm text-foreground-600">
          Você vai sair da sua conta só neste celular. Nos outros aparelhos a sessão continua
          aberta.
        </p>
        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={() => setSairConfirmar(false)}
            className="h-12 w-full cursor-pointer whitespace-nowrap rounded-2xl border border-secondary-300 bg-white text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-50 sm:flex-1"
          >
            Voltar
          </button>
          <button
            type="button"
            onClick={sairDeVez}
            className="h-12 w-full cursor-pointer whitespace-nowrap rounded-2xl bg-danger-500 text-sm font-semibold text-white transition-colors hover:bg-danger-600 sm:flex-1"
          >
            Sair
          </button>
        </div>
      </Modal>

      <EntrarNaFuncao aberto={funcaoAberto} aoFechar={() => setFuncaoAberto(false)} />
    </header>
  );
}